import axios from "axios";
import useSWR from "swr";
import { toChecksumAddress } from "ethereum-checksum-address"
import { sxbetRollupSubgraph, sxbetSubgraph } from "../../config";
import { tokens as sxnTokens } from "../../config/assets/sxn";
import { tokens as sxrTokens } from "../../config/assets/sxr";
import { fromWei } from "../../utils";
import { IStakingHarvestEvent, IStakingHarvestSummary, IStakingHarvestTx, STAKING_ACCOUNTS } from ".";
import { Chain } from "../../config/types";
import { ITimestampBlockMap, getBlocksByTimestamps } from "../subgraphs/blocks";
import { getTokenRates, getTokenRatesByBlock } from "../subgraphs/tokenRates";
import { IScope } from "../../state/app";
import { DEFAULT_END_DATE, DEFAULT_START_DATE } from "../subgraphs";

const query = /* GraphQL */`
  query($accounts: [String!]!, $startDate: Int, $endDate: Int) {
    harvestRewardsEvents(
      first: 1000
      orderBy: timestamp
      orderDirection: desc
      where: {      
        stakerAddress_in: $accounts
        timestamp_gte: $startDate
        timestamp_lte: $endDate
      }
    ) {
      id
      txHash
      timestamp
      amount
      tokenAddress
      stakerAddress
    }
  }
`;

async function dataFetcher([endpoint, query, chainId, variables] : [endpoint: string, query: string, chainId: Chain, variables: any]) {
  let data: any;
  const response = await axios.post(
    endpoint,
    JSON.stringify({
      query,
      variables,
    }),
    { headers: { "Content-Type": "application/json" }}
  );
  data = response.data;

  let events: IStakingHarvestEvent[] | undefined;
  let txs: IStakingHarvestTx[] | undefined;
  let summary: IStakingHarvestSummary | undefined;

   console.log("--------- data", data)

  if (data && data.data &&  data.data.harvestRewardsEvents && data.data.harvestRewardsEvents.length > 0) {
    const { data: { harvestRewardsEvents }} = data;
    const tokens = [...sxnTokens, ...sxrTokens];

    events = harvestRewardsEvents.map((e: any) => {
      const tokenAddress = toChecksumAddress(e.tokenAddress);
      const token = tokens.find(t => t.address === tokenAddress);
  
      return ({
        chainId,
        txHash: e.txHash,
        accountAddress: toChecksumAddress(e.stakerAddress),
        tokenAddress,
        timestamp: Number(e.timestamp),
        asset: token?.assetId,
        amount: fromWei(e.amount, token?.decimals), 
      })
    });
  
    summary = {}; 
    for (const { asset, tokenAddress, amount } of (events || [])) {
      if (!summary[asset]) {
        summary[asset] = {
          tokenAddress,
          amount: 0,
          amountUSD: 0,
          currentValueUSD: 0,
        };
      }
      summary[asset].amount += amount;
      // summary[asset].amountUSD += amountUSD || 0;
    }
    console.log("------ summary", summary);

    txs = (events || []).reduce((acc, e) => {
      let tx = acc.find(t => t.txHash === e.txHash)
      if (!tx) {
        tx = {
          timestamp: e.timestamp,
          // block: 0,
          chainId: e.chainId,
          txHash: e.txHash,
          accountAddress: e.accountAddress,
          amounts: {},
          amountsUSD: {},
          currentValuesUSD: {},
        }
        acc.push(tx)
      }
      tx.amounts[e.asset] = e.amount
      tx.amountsUSD[e.asset] = 0
      return acc
    }, [] as IStakingHarvestTx[])

    const timestamps = txs.map(e => e.timestamp)
    const timestampBlockMap: ITimestampBlockMap = await getBlocksByTimestamps(timestamps, chainId)

    console.log("------- timestampBlockMap", timestampBlockMap)

    const queryTokens = Object.keys(summary).map(key => summary![key].tokenAddress)
    const tokenRates = await getTokenRatesByBlock({
      chainId,
      queryTokens,
      timestampBlockMap
    })

    const currentTokenRates = await getTokenRates({
      chainId,
      queryTokens,
    })

    console.log("------ tokenRates", tokenRates, currentTokenRates)

    txs = txs.map(tx => {
      const block = timestampBlockMap[tx.timestamp]
      const amountsUSD = Object.keys(tx.amounts).reduce((acc: { [asset: string]: number }, symbol) => {
        const tokenAddress = summary![symbol].tokenAddress
        acc[symbol] = tx.amounts[symbol] * tokenRates[tokenAddress][tx.timestamp]
        return acc
      }, {})
      return {
        ...tx, 
        block,
        amountsUSD,
      }
    })

    for (let tx of txs) {
      // const block = timestampBlockMap[tx.timestamp]
      //tx.block = block
      for (let symbol of Object.keys(tx.amounts)) {
        const tokenAddress = summary![symbol].tokenAddress
        const amountUSD = tx.amounts[symbol] * tokenRates[tokenAddress][tx.timestamp]
        const currentValueUSD = tx.amounts[symbol] * currentTokenRates[symbol]
        //tx.amountsUSD[symbol] = amountUSD
        summary[symbol].amountUSD += amountUSD
        summary[symbol].currentValueUSD += currentValueUSD
      }
    }
  }

  // console.log("------ useSxnStaking sxn", txs)

  return events && txs && summary ? { txs, summary } : {};
}

export function useSxStaking(shouldFetch: boolean, scope: IScope, chainId: Chain) {
  const swrOptions = {
    revalidateOnFocus: false,
  };

  const endpoint = chainId === Chain.SXR ? sxbetRollupSubgraph : sxbetSubgraph;
  const variables = {
    accounts: STAKING_ACCOUNTS,
    startDate: scope.startDate || DEFAULT_START_DATE,
    endDate: scope.endDate || DEFAULT_END_DATE,
  }
  
  const fetchData = shouldFetch;

  const { data, error } = useSWR(
    fetchData ? [endpoint, query, chainId, variables] : null, 
    dataFetcher,
    swrOptions
  );

  if (error) {
    console.log("-------- fetching staking data error", error);
  }
  // console.log("--------- staking query", endpoint, query, variables)
  // console.log("--------- staking data", data);


  return { data, error };
}
